@if (loading) {
<loading />
} @else {

<section class="flex flex-col gap-1 mb-6">
  <h1 class="text-2xl font-bold text-slate-800">Panel General</h1>
  <p class="text-sm text-slate-500 capitalize">{{ currentDateLabel }}</p>
</section>

<div class="flex flex-col gap-6">

  <!-- KPI cards (each gated by the panel permission its data comes from) -->
  @if (showKpiSection) {
  <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">

    @if (permissions.documents) {
    <div class="bg-white border border-slate-200 rounded-xl p-5 flex flex-col gap-2.5 shadow-sm">
      <div class="flex items-center justify-between">
        <span class="text-xs font-semibold text-slate-500 uppercase tracking-wide">Ingresos del mes</span>
        <span class="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center text-sm">💰</span>
      </div>
      <div class="text-2xl font-extrabold text-slate-800" [centToEurDisplay]="kpis?.revenue_month?.value ?? 0"></div>
      @if (kpis?.revenue_month?.delta_pct !== null && kpis?.revenue_month?.delta_pct !== undefined) {
      <div class="text-xs font-semibold" [class.text-emerald-600]="(kpis!.revenue_month.delta_pct ?? 0) >= 0" [class.text-rose-600]="(kpis!.revenue_month.delta_pct ?? 0) < 0">
        {{ (kpis!.revenue_month.delta_pct ?? 0) >= 0 ? '▲' : '▼' }} {{ kpis!.revenue_month.delta_pct }}% vs mes anterior
      </div>
      } @else {
      <div class="text-xs font-medium text-slate-400">Sin datos del mes anterior</div>
      }
    </div>

    <div class="bg-white border border-slate-200 rounded-xl p-5 flex flex-col gap-2.5 shadow-sm">
      <div class="flex items-center justify-between">
        <span class="text-xs font-semibold text-slate-500 uppercase tracking-wide">Albaranes pendientes</span>
        <span class="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center text-sm">🧾</span>
      </div>
      <div class="text-2xl font-extrabold text-slate-800">{{ kpis?.active_orders?.value ?? 0 }}</div>
      <div class="text-xs font-semibold text-slate-500">
        + {{ kpis?.active_orders?.new_last_7_days ?? 0 }} nuevos esta semana
      </div>
    </div>
    }

    @if (permissions.products) {
    <div class="bg-white border border-slate-200 rounded-xl p-5 flex flex-col gap-2.5 shadow-sm">
      <div class="flex items-center justify-between">
        <span class="text-xs font-semibold text-slate-500 uppercase tracking-wide">Stock crítico</span>
        <span class="w-8 h-8 rounded-lg bg-rose-50 flex items-center justify-center text-sm">📦</span>
      </div>
      <div class="text-2xl font-extrabold text-slate-800">{{ kpis?.low_stock?.value ?? 0 }} SKUs</div>
      <div class="text-xs font-semibold text-slate-500">stock ≤ {{ kpis?.low_stock?.threshold ?? 0 }} uds.</div>
    </div>
    }

    @if (permissions.clients) {
    <div class="bg-white border border-slate-200 rounded-xl p-5 flex flex-col gap-2.5 shadow-sm">
      <div class="flex items-center justify-between">
        <span class="text-xs font-semibold text-slate-500 uppercase tracking-wide">Clientes nuevos</span>
        <span class="w-8 h-8 rounded-lg bg-violet-50 flex items-center justify-center text-sm">👥</span>
      </div>
      <div class="text-2xl font-extrabold text-slate-800">{{ kpis?.new_clients?.value ?? 0 }}</div>
      @if (kpis?.new_clients?.delta_pct !== null && kpis?.new_clients?.delta_pct !== undefined) {
      <div class="text-xs font-semibold" [class.text-emerald-600]="(kpis!.new_clients.delta_pct ?? 0) >= 0" [class.text-rose-600]="(kpis!.new_clients.delta_pct ?? 0) < 0">
        {{ (kpis!.new_clients.delta_pct ?? 0) >= 0 ? '▲' : '▼' }} {{ kpis!.new_clients.delta_pct }}% vs mes anterior
      </div>
      } @else {
      <div class="text-xs font-medium text-slate-400">Sin datos del mes anterior</div>
      }
    </div>
    }

  </div>
  }

  <!-- Quick access -->
  @if (quickActions.length > 0) {
  <div class="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
    <div class="text-base font-bold text-slate-800">Accesos rápidos</div>
    <div class="text-xs text-slate-500 mb-4">Acciones frecuentes por módulo</div>
    <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3.5">
      @for (qa of quickActions; track qa.label) {
      <a [routerLink]="qa.link" class="flex flex-col items-center gap-2.5 py-4 px-2 border border-slate-200 rounded-lg hover:border-blue-300 hover:bg-blue-50/40 transition-colors text-center">
        <span class="w-10 h-10 rounded-lg bg-slate-50 flex items-center justify-center">
          <img [src]="'/icons/' + qa.icon" [alt]="qa.label" class="w-5 h-5">
        </span>
        <span class="text-xs font-semibold text-slate-600">{{ qa.label }}</span>
      </a>
      }
    </div>
  </div>
  }

  <!-- Charts row 1: bar + donut (both driven by document/invoice data) -->
  @if (permissions.documents) {
  <div class="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-5">

    <div class="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
      <div class="flex items-center justify-between mb-5">
        <div>
          <div class="text-base font-bold text-slate-800">Ventas por mes</div>
          <div class="text-xs text-slate-500">Facturación, año actual vs anterior</div>
        </div>
        <div class="flex gap-4 text-xs text-slate-500">
          <span class="flex items-center gap-1.5"><span class="w-2 h-2 rounded-sm bg-blue-600 inline-block"></span>Este año</span>
          <span class="flex items-center gap-1.5"><span class="w-2 h-2 rounded-sm bg-slate-300 inline-block"></span>Año anterior</span>
        </div>
      </div>
      @if (barGroups.length > 0) {
      <svg [attr.viewBox]="'0 0 ' + chartW + ' ' + barH" class="w-full" [style.height.px]="barH">
        @for (g of barGridLines; track $index) {
        <line [attr.x1]="30" [attr.x2]="chartW - 10" [attr.y1]="g" [attr.y2]="g" stroke="#EEF1F6" stroke-width="1" />
        }
        @for (b of barGroups; track b.label) {
        <rect [attr.x]="b.prevX" [attr.y]="b.prevY" [attr.width]="10" [attr.height]="b.prevH" rx="2" fill="#CBD5E1" />
        <rect [attr.x]="b.curX" [attr.y]="b.curY" [attr.width]="10" [attr.height]="b.curH" rx="2" fill="#2563EB" />
        <text [attr.x]="b.labelX" [attr.y]="barH - 4" font-size="10" fill="#8A97B3" text-anchor="middle">{{ b.label }}</text>
        }
      </svg>
      }
    </div>

    <div class="bg-white border border-slate-200 rounded-xl p-6 shadow-sm flex flex-col">
      <div class="text-base font-bold text-slate-800">Distribución de ingresos</div>
      <div class="text-xs text-slate-500 mb-3">Por familia, mes actual</div>
      @if (donutSegments.length > 0) {
      <div class="flex-1 flex items-center justify-center">
        <svg [attr.viewBox]="'0 0 ' + donutSize + ' ' + donutSize" [style.width.px]="donutSize" [style.height.px]="donutSize">
          @for (seg of donutSegments; track $index) {
          <circle [attr.cx]="donutCx" [attr.cy]="donutCy" [attr.r]="donutR" fill="none" [attr.stroke]="seg.color"
            [attr.stroke-width]="donutStroke" [attr.stroke-dasharray]="seg.dasharray" [attr.stroke-dashoffset]="seg.dashoffset"
            [attr.transform]="'rotate(-90 ' + donutCx + ' ' + donutCy + ')'" stroke-linecap="butt" />
          }
          <text [attr.x]="donutCx" [attr.y]="donutCy - 4" font-size="18" font-weight="800" fill="#101A2E" text-anchor="middle">{{ donutTotalLabel }}</text>
          <text [attr.x]="donutCx" [attr.y]="donutCy + 14" font-size="10" fill="#8A97B3" text-anchor="middle">Total mes</text>
        </svg>
      </div>
      <div class="flex flex-col gap-2">
        @for (row of revenueByCategory; track row.label) {
        <div class="flex items-center justify-between text-xs">
          <span class="flex items-center gap-2 text-slate-600 font-medium">
            <span class="w-2 h-2 rounded-full inline-block" [style.background]="row.color"></span>{{ row.label }}
          </span>
          <span class="text-slate-500 font-semibold">{{ row.pct }}%</span>
        </div>
        }
      </div>
      } @else {
      <div class="flex-1 flex items-center justify-center text-sm text-slate-400 py-8">Sin facturación este mes</div>
      }
    </div>
  </div>
  }

  <!-- Charts row 2: line (documents) + recent activity (superadmin only, like /activity-logs) -->
  @if (showTrendRow) {
  <div [class]="(permissions.documents && permissions.activity) ? 'grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-5' : 'grid grid-cols-1 gap-5'">

    @if (permissions.documents) {
    <div class="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
      <div class="text-base font-bold text-slate-800">Tendencia de pedidos</div>
      <div class="text-xs text-slate-500 mb-4">Últimos 12 meses</div>
      @if (linePath) {
      <svg [attr.viewBox]="'0 0 ' + chartW + ' ' + lineH" class="w-full" [style.height.px]="lineH">
        @for (g of lineGridLines; track $index) {
        <line [attr.x1]="30" [attr.x2]="chartW - 10" [attr.y1]="g" [attr.y2]="g" stroke="#EEF1F6" stroke-width="1" />
        }
        <path [attr.d]="lineArea" fill="#2563EB" opacity="0.08" />
        <path [attr.d]="linePath" fill="none" stroke="#2563EB" stroke-width="2.5" />
        @for (d of lineDots; track $index) {
        <circle [attr.cx]="d.x" [attr.cy]="d.y" r="3" fill="#2563EB" />
        }
        @for (l of lineLabels; track $index) {
        <text [attr.x]="l.x" [attr.y]="lineH - 4" font-size="10" fill="#8A97B3" text-anchor="middle">{{ l.label }}</text>
        }
      </svg>
      }
    </div>
    }

    @if (permissions.activity) {
    <div class="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
      <div class="text-base font-bold text-slate-800 mb-3.5">Actividad reciente</div>
      @if (recentActivity.length > 0) {
      <div class="flex flex-col gap-3.5">
        @for (a of recentActivity; track $index) {
        <div class="flex gap-3 items-start">
          <div class="w-2 h-2 rounded-full mt-1.5 shrink-0"
            [class.bg-emerald-500]="a.action === 'create'"
            [class.bg-blue-600]="a.action === 'update'"
            [class.bg-rose-500]="a.action === 'delete'"></div>
          <div class="flex flex-col gap-0.5">
            <span class="text-sm font-semibold text-slate-700">{{ activityLabel(a) }}</span>
            <span class="text-[11.5px] text-slate-400">{{ timeAgo(a.created_at) }}</span>
          </div>
        </div>
        }
      </div>
      } @else {
      <div class="text-sm text-slate-400 py-4">Sin actividad reciente</div>
      }
    </div>
    }
  </div>
  }

</div>

}
